import { Fragment } from "react";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "@/utils/classnames";
import moment from "moment";
import { formatPhoneUS } from "@/utils/functions";
import {
  addresseeEmail,
  addresseeNamePriority,
  addresseePhone,
} from "@/utils/invoice";
import { convertThousandSeparator } from "@/scripts/functions";
import { ChevronDown, Info, XCircle } from "lucide-react";
import { InvoiceStatusBadge } from "./invoice-status-badge";
import {
  DRAFT_INVOICE_STATUS,
  PAID_INVOICE_STATUS,
  PAID_OFF_PLATFORM_STATUS,
} from "@/constants/invoice";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { useRefundDrawer } from "@/hooks/invoice/use-refund-drawer";

function Loading() {
  return (
    <div>
      <div role="status" className="px-6 py-8 animate-pulse">
        <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-48 mb-4"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[330px] mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[300px] mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px]"></div>
        <span className="sr-only">Loading...</span>
      </div>
      <div
        role="status"
        className=" p-4 space-y-4 border border-gray-200 divide-y divide-gray-200 rounded shadow animate-pulse dark:divide-gray-700 md:p-6 dark:border-gray-700"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );
}

function Error() {
  return (
    <div className="rounded-md bg-red-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <XCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            There was an error loading the quick pay details.
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <ul role="list" className="list-disc space-y-1 pl-5">
              <li>Make sure your internet connection is stable</li>
              <li>Try closing and opening the drawer again</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

interface QuickPayViewDrawerProps {
  invoice: any;
  open: boolean;
  handleClose: () => void;
  isLoading: boolean;
  isError: boolean;
}

export default function QuickPayViewDrawer({
  invoice,
  open,
  handleClose,
  isLoading,
  isError,
}: QuickPayViewDrawerProps) {
  const { handleOpen: handleRefundOpen } = useRefundDrawer();

  const isPaidCC =
    [PAID_INVOICE_STATUS, PAID_OFF_PLATFORM_STATUS].includes(
      invoice?.invoice_info?.invoice_status,
    ) &&
    invoice?.invoice_info?.invoice_payment_method_type?.toLowerCase() ===
      "credit_card";

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleClose}>
          <div className="fixed inset-0" />

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="relative pointer-events-auto w-screen max-w-2xl">
                    <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                      <div className="px-4 py-6 sm:px-6 bg-skin-primary">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-base font-semibold leading-6 text-white">
                            Quick Pay
                            {!isLoading &&
                            !isError &&
                            invoice?.invoice_info?.invoice_number
                              ? ` #${invoice?.invoice_info?.invoice_number}`
                              : ""}
                          </Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="relative rounded-md text-gray-50 hover:text-gray-300 focus:ring-2 focus:ring-white"
                              onClick={handleClose}
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* Main */}
                      {isLoading ? (
                        <Loading />
                      ) : isError ? (
                        <Error />
                      ) : (
                        <div className="divide-y divide-gray-200">
                          <div className="pb-6">
                            <div className="flex-col space-y-4 px-4 sm:px-6 min-h-full">
                              <div className="flex justify-between items-center mt-6 sm:flex-1">
                                <div className="flex items-center">
                                  <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
                                    $
                                    {convertThousandSeparator(
                                      Number(
                                        invoice?.invoice_info.invoice_total,
                                      ),
                                    )}
                                  </h3>
                                  <InvoiceStatusBadge
                                    status={
                                      invoice?.invoice_info
                                        .invoice_frequency === "scheduled"
                                        ? "Scheduled"
                                        : invoice?.invoice_info
                                            .invoice_status ||
                                          DRAFT_INVOICE_STATUS
                                    }
                                  />
                                  {invoice?.invoice_info
                                    .invoice_payment_method_type &&
                                    invoice?.invoice_info
                                      .invoice_payment_method_type !==
                                      "none" && (
                                      <span
                                        className={cn(
                                          "inline-flex items-center rounded-md bg-purple-50 ml-2 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20",
                                        )}
                                      >
                                        <span className="">
                                          {invoice?.invoice_info.invoice_payment_method_type?.toLowerCase() ===
                                          "ach"
                                            ? "ACH"
                                            : invoice?.invoice_info.invoice_payment_method_type
                                                .replace(/^[-_]*(.)/, (_, c) =>
                                                  c.toUpperCase(),
                                                )
                                                .replace(
                                                  /[-_]+(.)/g,
                                                  (_, c) =>
                                                    " " + c.toUpperCase(),
                                                )}
                                        </span>
                                      </span>
                                    )}
                                </div>
                                {isPaidCC ? (
                                  <Menu
                                    as="div"
                                    className="relative inline-block text-left"
                                  >
                                    <Menu.Button className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                      Options
                                      <ChevronDown
                                        className="-mr-1 h-5 w-5 text-gray-400"
                                        aria-hidden="true"
                                      />
                                    </Menu.Button>
                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-100"
                                      enterFrom="transform opacity-0 scale-95"
                                      enterTo="transform opacity-100 scale-100"
                                      leave="transition ease-in duration-75"
                                      leaveFrom="transform opacity-100 scale-100"
                                      leaveTo="transform opacity-0 scale-95"
                                    >
                                      <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                        <div className="py-1">
                                          <Menu.Item>
                                            {({ active }) => (
                                              <div
                                                onClick={() =>
                                                  handleRefundOpen(
                                                    invoice?.invoice_id,
                                                  )
                                                }
                                                className={cn(
                                                  active
                                                    ? "bg-gray-100 text-gray-900"
                                                    : "text-gray-700",
                                                  "block px-4 py-2 text-sm cursor-pointer",
                                                )}
                                              >
                                                Issue Refund
                                              </div>
                                            )}
                                          </Menu.Item>
                                        </div>
                                      </Menu.Items>
                                    </Transition>
                                  </Menu>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="px-4 py-5 sm:px-6 sm:py-0 sm:pb-8">
                            {/* QuickPay Details */}
                            <dl className="mt-6 grid grid-cols-1 text-sm leading-6 sm:grid-cols-2">
                              <div className="sm:pr-4 mb-6">
                                <dt className="font-semibold text-gray-900">
                                  Name on Order
                                </dt>
                                <dd className="mt-2 text-gray-500">
                                  {
                                    invoice?.invoice_info?.invoice_to
                                      ?.business_name
                                  }
                                </dd>
                              </div>

                              <div className="sm:pl-4 mb-6">
                                <dt className="font-semibold text-gray-900">
                                  Reference #
                                </dt>
                                <dd className="mt-2 text-gray-500">
                                  {invoice?.invoice_info?.invoice_external_id}
                                </dd>
                              </div>

                              <div className="border-t border-gray-900/5 pt-6 sm:pr-4 flex gap-x-1 col-span-2">
                                <dt className="inline text-gray-500">
                                  {invoice?.invoice_info?.invoice_send_date ? (
                                    <p>Issued on</p>
                                  ) : (
                                    <p className="flex items-center gap-x-2">
                                      <span>No issued date</span>

                                      <HoverCard>
                                        <HoverCardTrigger>
                                          <Info className="w-4 h-4 text-gray-500 hover:text-gray-700" />
                                        </HoverCardTrigger>
                                        <HoverCardContent>
                                          The issue date is the day the invoice
                                          is sent, or the day it is scheduled to
                                          be sent.
                                        </HoverCardContent>
                                      </HoverCard>
                                    </p>
                                  )}
                                </dt>{" "}
                                <dd className="inline text-gray-700">
                                  <time>
                                    {invoice?.invoice_info?.invoice_send_date &&
                                      moment(
                                        invoice?.invoice_info
                                          ?.invoice_send_date,
                                      ).format("LL")}
                                  </time>
                                </dd>
                              </div>

                              <div className="mt-8 sm:mt-6 sm:border-t sm:border-gray-900/5 sm:pt-6 col-span-2">
                                <dt className="font-semibold text-gray-900">
                                  Billing Contact Information
                                </dt>
                                <dd className="mt-2 text-gray-500">
                                  <span className="font-medium text-gray-900">
                                    {addresseeNamePriority(invoice)}
                                  </span>

                                  <br />
                                  {invoice?.invoice_info?.invoice_to
                                    ?.business_address || ""}
                                  <br />
                                  {`${
                                    invoice?.invoice_info?.invoice_to
                                      ?.business_city || ""
                                  }, ${
                                    invoice?.invoice_info?.invoice_to
                                      ?.business_state || ""
                                  } ${
                                    invoice?.invoice_info?.invoice_to
                                      ?.business_zip || ""
                                  }`.trim()}
                                  <br />
                                  {formatPhoneUS(addresseePhone(invoice))}
                                  <br />
                                  {addresseeEmail(invoice) || ""}
                                  <br />
                                </dd>
                              </div>
                            </dl>
                          </div>
                        </div>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
